import ChatAPI from '../packs/chat_api'

// Undo a sent message: shared by the master chat and the permit chat. The
// server decides (ChatMessage#undo_error); this only offers the button while it
// can still succeed — own message, within its window, nobody has replied.
const BUTTON_CLASS = 'chat-undo-btn'
const MODAL_ID = 'undoMessageConfirmationModal'
const undoneCallbacks = []
let initialized = false

export function buildUndoButton(message, currentUserId) {
  if (!message || !message.id) return null
  if (Number(message.user_id) !== Number(currentUserId)) return null
  if (message.message_type === 'system' || message.message_type === 4) return null
  if (message.undoable === false) return null

  const until = Date.parse(message.undoable_until || '')
  if (Number.isNaN(until) || until <= Date.now()) return null

  const button = document.createElement('button')
  button.type = 'button'
  button.className = BUTTON_CLASS
  button.textContent = 'Undo'
  button.title = 'Remove this message for everyone. Available for 3 hours, until someone replies.'
  button.dataset.conversationId = message.chat_conversation_id
  button.dataset.messageId = message.id
  button.dataset.undoUntil = message.undoable_until
  return button
}

// A message from someone else is a reply to everything above it.
export function clearUndoButtons(container) {
  container?.querySelectorAll(`.${BUTTON_CLASS}`).forEach((button) => button.remove())
}

export function removeUndoneMessage(messageId) {
  document.querySelectorAll(`.message-wrapper[data-message-id="${messageId}"]`).forEach((wrapper) => {
    removeFileItems(wrapper)
    wrapper.remove()
  })
}

// The permit chat's Files panel lists attachments without a message id, so
// match them by URL (signed query strings differ between renders).
function removeFileItems(wrapper) {
  const paths = new Set()
  wrapper.querySelectorAll('[src], [href], [data-media-url]').forEach((el) => {
    const url = el.getAttribute('data-media-url') || el.getAttribute('src') || el.getAttribute('href')
    if (url && url !== '#') paths.add(url.split('?')[0])
  })
  if (paths.size === 0) return

  document.querySelectorAll('.files-list .file-item').forEach((item) => {
    const link = item.querySelector('[href]')
    if (link && paths.has(link.getAttribute('href').split('?')[0])) item.remove()
  })
}

function expireButtons() {
  document.querySelectorAll(`.${BUTTON_CLASS}`).forEach((button) => {
    const until = Date.parse(button.dataset.undoUntil || '')
    if (!Number.isNaN(until) && until <= Date.now()) button.remove()
  })
}

export function onMessageUndone(callback) {
  undoneCallbacks.push(callback)
}

export function initChatUndo() {
  if (initialized) return
  initialized = true

  document.addEventListener('click', (event) => {
    const button = event.target.closest(`.${BUTTON_CLASS}`)
    if (!button) return
    event.preventDefault()
    event.stopPropagation()
    showConfirmModal(button)
  }, true)

  setInterval(expireButtons, 30 * 1000)
}

async function undo(button) {
  const { conversationId, messageId } = button.dataset
  button.disabled = true
  const result = await ChatAPI.undoMessage(conversationId, messageId)
  if (result.ok) {
    removeUndoneMessage(messageId)
    undoneCallbacks.forEach((callback) => callback(conversationId, messageId))
  } else {
    // A 422 means the window closed (time or a reply) — the button is stale.
    if (result.status === 422) button.remove()
    else button.disabled = false
    showError(result.error || 'Could not undo this message. Please try again.')
  }
}

function showError(message) {
  if (typeof window.show_ajax_error === 'function') window.show_ajax_error(message)
  else console.error(message)
}

// Same markup/styles as the Delete Chat and Leave Group confirmations.
function showConfirmModal(button) {
  hideConfirmModal()

  const modal = document.createElement('div')
  modal.id = MODAL_ID
  modal.className = 'chat-confirmation-modal'
  modal.setAttribute('role', 'dialog')
  modal.innerHTML = `
    <div class="chat-confirmation-backdrop" data-undo-dismiss></div>
    <div class="chat-confirmation-dialog">
      <div class="chat-confirmation-content">
        <div class="chat-confirmation-header">
          <div class="header-icon danger">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 14 4 9 9 4"></polyline>
              <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
            </svg>
          </div>
          <h3 class="chat-confirmation-title">Undo Message</h3>
          <button class="chat-confirmation-close" type="button" data-undo-dismiss aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="chat-confirmation-body">
          <p>Are you sure you want to undo this message?</p>
          <p class="text-muted">It will be removed for everyone in this chat, along with any files attached to it.</p>
        </div>
        <div class="chat-confirmation-footer">
          <button class="btn-cancel" type="button" data-undo-dismiss>Cancel</button>
          <button class="btn-confirm btn-danger" type="button" data-undo-confirm>Undo</button>
        </div>
      </div>
    </div>`

  modal.addEventListener('click', (event) => {
    if (event.target.closest('[data-undo-dismiss]')) {
      hideConfirmModal()
    } else if (event.target.closest('[data-undo-confirm]')) {
      hideConfirmModal()
      undo(button)
    }
  })
  document.addEventListener('keydown', closeOnEscape)
  document.body.appendChild(modal)
}

function hideConfirmModal() {
  document.getElementById(MODAL_ID)?.remove()
  document.removeEventListener('keydown', closeOnEscape)
}

function closeOnEscape(event) {
  if (event.key === 'Escape') hideConfirmModal()
}
